/**
 * A named-catalogue chart with its range picker.
 *
 * The browser sends a NAME and a range from a fixed enum. There is no place in
 * this component — or anywhere else in the bundle — where a PromQL string could
 * be assembled and sent; that is the point of `/api/series/:name`.
 *
 * A `blocked` entry gets a panel saying so, with the recorded reason and the
 * dashed container, instead of an empty chart. An empty chart is
 * indistinguishable from a healthy one — which is the entire failure this
 * dashboard exists to avoid, in its most literal form.
 */

import { Suspense, lazy, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useApi } from '../lib/api';
import { Card, Placeholder } from './primitives';
import { explain, formatNumber, labelFor } from '../lib/signal';
import type { ChartSeries } from './Chart';

const Chart = lazy(() => import('./Chart'));

const RANGES = ['1h', '6h', '24h', '7d'] as const;
type Range = (typeof RANGES)[number];

interface JsonSample {
  t: number;
  v: number | 'NaN' | '+Inf' | '-Inf';
}
interface JsonSeries {
  labels: Record<string, string>;
  samples: JsonSample[];
}
interface SeriesResponse {
  name: string;
  kind: string;
  description: string;
  range: Range;
  stepSeconds: number;
  collectedAt: string;
  cached: boolean;
  series:
    | { status: 'ok'; value: JsonSeries[] }
    | {
        status: string;
        reason?: string;
        detail?: string;
        gate?: string;
        kind?: string;
        message?: string;
      };
}

/**
 * The name a series shows in the legend, the tooltip and the numbers table.
 *
 * A query whose result carries no labels beyond `__name__` — `scrape_targets_up`
 * is one — used to come back as "series 1", which is a name that tells an
 * operator nothing at 03:00. When there is exactly one such series, it borrows
 * the panel's own title instead.
 */
function labelOf(labels: Record<string, string>, index: number, fallback: string): string {
  const parts = Object.entries(labels)
    .filter(([k]) => k !== '__name__')
    .map(([, v]) => v);
  if (parts.length > 0) return parts.join(' / ');
  return index === 0 ? fallback : `${fallback} (${String(index + 1)})`;
}

export function SeriesChart({
  name,
  title,
  unit,
  defaultRange = '6h',
  maxSeries = 8,
  height = 200,
}: {
  name: string;
  title: string;
  unit?: string;
  defaultRange?: Range;
  maxSeries?: number;
  height?: number;
}): ReactNode {
  const [range, setRange] = useState<Range>(defaultRange);
  const { data, error, loading } = useApi<SeriesResponse>(
    `/api/series/${encodeURIComponent(name)}?range=${range}`,
    60_000,
  );

  const realSeries: ChartSeries[] = useMemo(() => {
    if (data === null || data.series.status !== 'ok') return [];
    const value = (data.series as { status: 'ok'; value: JsonSeries[] }).value;
    return value.slice(0, maxSeries).map((s, index) => ({
      name: labelOf(s.labels, index, title),
      points: s.samples.map((sample): [string, number | string] => [
        new Date(sample.t * 1000).toISOString(),
        sample.v,
      ]),
    }));
  }, [data, maxSeries, title]);

  /**
   * The latest finite value, printed as a number in the header.
   *
   * Only for a SINGLE series — with eight series there is no "the" value, and a
   * headline number that silently belongs to one of them is worse than none.
   * When the last sample is non-finite the header says so rather than reaching
   * further back for a nicer number.
   */
  const headline = useMemo(() => {
    if (realSeries.length !== 1) return null;
    const points = realSeries[0]?.points ?? [];
    const last = points.at(-1);
    if (last === undefined) return null;
    return typeof last[1] === 'number' && Number.isFinite(last[1])
      ? `${formatNumber(last[1], 2)}${unit === undefined ? '' : ` ${unit}`}`
      : String(last[1]);
  }, [realSeries, unit]);

  const truncated = useMemo(() => {
    if (data === null || data.series.status !== 'ok') return 0;
    return Math.max(0, (data.series as { value: JsonSeries[] }).value.length - maxSeries);
  }, [data, maxSeries]);

  const picker = (
    <div
      role="group"
      aria-label={`time range for ${title}`}
      className="flex overflow-hidden rounded-lg border border-line"
    >
      {RANGES.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => {
            setRange(r);
          }}
          aria-pressed={range === r}
          className={`num min-w-11 px-2.5 text-2xs font-medium transition-colors ${
            range === r ? 'bg-surface-3 text-ink' : 'text-ink-3 hover:bg-surface-2 hover:text-ink-2'
          }`}
        >
          {r}
        </button>
      ))}
    </div>
  );

  return (
    <Card
      title={
        /*
         * The VALUE outranks the TITLE. They used to sit at the same size and
         * the same weight, so a panel headed `Scrape targets up  90.38` read as
         * two labels rather than as a label and its reading — and the reading is
         * what an operator is here for. The number is now a step larger, at full
         * ink strength, against a title that stays at card-title size.
         */
        <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <span className="text-ink-2">{title}</span>
          {headline !== null && (
            <span className="num text-lg font-semibold tracking-tight text-ink">{headline}</span>
          )}
        </span>
      }
      subtitle={data?.description ?? name}
      right={picker}
    >
      {error !== null && (
        <Placeholder tone="bad" title="could not load this chart">
          {error.message}
        </Placeholder>
      )}
      {error === null && loading && (
        <div
          className="animate-pulse rounded-xl border border-line bg-surface-2/50"
          style={{ height }}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={`loading ${title}…`}
        />
      )}
      {error === null && !loading && data !== null && data.series.status !== 'ok' && (
        <Placeholder status={data.series.status} title={labelFor(data.series.status)}>
          {explain(data.series)}
        </Placeholder>
      )}
      {error === null &&
        !loading &&
        data !== null &&
        data.series.status === 'ok' &&
        (realSeries.length === 0 ? (
          <Placeholder status="empty" title="no series to plot">
            the query succeeded and returned no series in this window
          </Placeholder>
        ) : (
          <Suspense
            fallback={
              <div
                className="animate-pulse rounded-xl border border-line bg-surface-2/50"
                style={{ height }}
                role="status"
                aria-live="polite"
                aria-busy="true"
                aria-label={`drawing ${title}…`}
              />
            }
          >
            <Chart
              series={realSeries}
              height={height}
              stepSeconds={data.stepSeconds}
              ariaLabel={`${title} over the last ${range}`}
              {...(unit === undefined ? {} : { unit })}
            />
          </Suspense>
        ))}
      {truncated > 0 && (
        <p className="mt-2 text-2xs text-ink-3">
          {truncated} further series are not drawn; the numbers table shows only what is.
        </p>
      )}
    </Card>
  );
}
